import type { NavigateFunction } from "react-router-dom";
import { isAccessRestrictedFacility } from "@utils/facilityRestricted";
import {
  readPreferredFacilityForPricing,
  writePreferredFacilityForPricing,
} from "@utils/pricingFacilityContext";
import { withSnfCheckoutType } from "@utils/snfCheckoutType";

/** AB#10916 leftover Mackay unpaid Access Restricted wall (CI NPI 1760402663). */

/** Session key: leftover unpaid sites cleared by a successful promo Pay Now. */
export const PROMO_CHECKOUT_CLEARED_RESTRICTED_KEY =
  "promo_checkout_cleared_restricted";

/** Snapshot so Pay Now stays on leftover Mackay after sibling Arco is selected. */
export const LEFTOVER_UNPAID_FACILITY_KEY = "leftover_unpaid_facility";

export type UnpaidAccessAction = "active" | "select-plan" | "restricted" | "none";

export type RestrictedUpdatePaymentAction = {
  pathname: "/dashboard/pricing" | "/dashboard/pricing/order-summary";
  search: string;
  state: {
    fromRestrictedUpdatePayment: true;
    fromPreferredFacilityId?: number;
  };
};

export type PromoCheckoutClearedIdentity = {
  npi?: string;
  facilityName?: string;
  facilityId?: number;
};

function _norm(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function isTrialFacility(facility: any): boolean {
  const raw = facility?.is_trial ?? facility?.is_trail;
  return raw === true || _norm(raw) === "true";
}

function facilityDetails(facility: any): Record<string, unknown> {
  const details = facility?.facility_details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return {};
}

function firstNonemptyRaw(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

/** Trailing 10-digit NPI. Prefer npi_* over Medicare CCN / empty ccn_number. */
export function leftoverFacilityNpi(facility: any): string {
  const d = facilityDetails(facility);
  const candidates = [
    facility?.npi_number,
    facility?.npi,
    d.npi_number,
    d.npi,
    facility?.ccn_number,
    d.ccn_number,
    facility?.facility_unique_id,
    d.facility_unique_id,
  ];
  let fallback = "";
  for (const raw of candidates) {
    const digits = String(raw ?? "").replace(/\D/g, "");
    if (!digits) continue;
    if (digits.length >= 10) return digits.slice(-10);
    if (!fallback) fallback = digits;
  }
  return fallback;
}

/** 10-digit leftover NPI from leftover snapshot / preferred / selected, never sibling. */
export function resolveLeftoverPayNowNpi(...sources: any[]): string {
  const leftover = pickLeftoverUnpaidSource(...sources);
  const leftoverNpi = leftoverFacilityNpi(leftover);
  if (leftoverNpi.length === 10) return leftoverNpi;
  for (const source of sources) {
    const npi = leftoverFacilityNpi(source);
    if (npi.length === 10) return npi;
  }
  return leftoverFacilityNpi(sources.find(Boolean));
}

export function leftoverFacilityName(facility: any): string {
  const d = facilityDetails(facility);
  return _norm(
    facility?.facility_name ??
      facility?.site_name ??
      d.facility_name ??
      d.site_name,
  );
}

export function leftoverFacilityId(facility: any): number | null {
  const raw =
    facility?.facility_id ??
    facilityDetails(facility).facility_id ??
    facility?.id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** user-preferred-facilities PK after admin-promo hydrate — not rules facility_id. */
export function leftoverPreferredPk(facility: any): number | null {
  const raw = facility?.user_preferred_facility_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function leftoverIdentity(facility: any): PromoCheckoutClearedIdentity {
  const npi = leftoverFacilityNpi(facility);
  const facilityName = leftoverFacilityName(facility);
  const facilityId = leftoverFacilityId(facility);
  return {
    ...(npi ? { npi } : {}),
    ...(facilityName ? { facilityName } : {}),
    ...(facilityId != null ? { facilityId } : {}),
  };
}

function identitiesMatch(
  stored: PromoCheckoutClearedIdentity,
  facility: any,
): boolean {
  const current = leftoverIdentity(facility);
  if (stored.npi && current.npi && stored.npi === current.npi) return true;
  if (
    stored.facilityId != null &&
    current.facilityId != null &&
    stored.facilityId === current.facilityId
  ) {
    return true;
  }
  return namesLooselyMatch(stored.facilityName || "", current.facilityName || "");
}

/** True when two facility blobs are the same leftover/pricing identity. */
export function leftoverFacilitiesMatch(a: any, b: any): boolean {
  if (!a || !b) return false;
  return identitiesMatch(leftoverIdentity(a), b);
}

export function clearLeftoverUnpaidIdentity(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(LEFTOVER_UNPAID_FACILITY_KEY);
  } catch {
    // ignore
  }
}

/** Drop leftover snapshot when user picks a different pending/unpaid facility. */
export function clearLeftoverUnpaidIfDifferentFacility(facility: any): void {
  const leftover = readLeftoverUnpaidIdentity();
  if (leftover && facility && !leftoverFacilitiesMatch(leftover, facility)) {
    clearLeftoverUnpaidIdentity();
  }
}

function toPersistedTypeId(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function leftoverDiscountCode(facility: any): string {
  const d = facilityDetails(facility);
  return firstNonemptyRaw(
    facility?.discount_coupon_name,
    facility?.discount_coupon,
    d.discount_coupon_name,
    d.discount_coupon,
  );
}

function leftoverDiscountCouponId(facility: any): number | null {
  const raw =
    facility?.discount_coupon_id ?? facilityDetails(facility).discount_coupon_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Admin promo name or linked coupon id (MCM list often returns id with empty name). */
export function leftoverHasAdminPromo(facility: any): boolean {
  return !!leftoverDiscountCode(facility) || leftoverDiscountCouponId(facility) != null;
}

function matchesPersistedLeftoverUnpaidIdentity(facility: any): boolean {
  const stored = readLeftoverUnpaidIdentity();
  if (!stored || !facility) return false;
  if (
    stored.is_plan_active === true ||
    isPaidTransaction(_norm(stored.transaction_status))
  ) {
    return false;
  }
  return identitiesMatch(leftoverIdentity(stored), facility);
}

function isPaidTransaction(tx: string): boolean {
  return new Set(["ok", "success", "completed", "paid", "succeeded"]).has(tx);
}

/**
 * Leftover DBA / never-completed checkout. Not trial-expired or payment-failed
 * unless admin-row promo is already on the leftover (CI Mackay FREE_30_*).
 * CI Mackay leftover: is_plan_active false, promo already on admin row.
 */
export function isLeftoverUnpaidFacility(facility: any): boolean {
  if (!facility) return false;
  const planStatus = _norm(facility?.plan_status);
  const tx = _norm(facility?.transaction_status);
  if (facility?.is_plan_active === true || isPaidTransaction(tx)) return false;
  const unpaidExpired =
    facility?.is_plan_active === false ||
    planStatus === "expired" ||
    tx === "unpaid" ||
    tx === "pending" ||
    tx === "processing";
  // Leftover DBA with admin promo (name or coupon id) or leftover snapshot:
  // never-completed checkout even if API stamps is_trial on my-facilities.
  if (unpaidExpired && leftoverHasAdminPromo(facility)) return true;
  if (unpaidExpired && matchesPersistedLeftoverUnpaidIdentity(facility)) {
    return true;
  }
  if (isTrialFacility(facility) && planStatus === "expired") return false;
  if (
    (tx === "failed" || tx === "canceled" || tx === "cancelled") &&
    planStatus === "expired"
  ) {
    return false;
  }
  return unpaidExpired;
}

/** Preferred-facility list from /user-preferred-facilities (result.data or data). */
export function preferredFacilityRowsFromResponse(res: any): any[] {
  const fromResult = res?.result?.data;
  const fromTopLevel = res?.data;
  if (Array.isArray(fromResult)) return fromResult;
  if (Array.isArray(fromTopLevel)) return fromTopLevel;
  return [];
}

/** Brand / generic tokens that must not bind leftover promo across siblings. */
const LEFTOVER_NAME_STOPWORDS = new Set([
  "providence",
  "health",
  "clinic",
  "hospital",
  "medical",
  "center",
  "facility",
  "healthcare",
  "services",
  "system",
  "group",
  "the",
  "of",
  "and",
]);

function distinctiveLeftoverNameTokens(name: string): string[] {
  return name
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 4 && !LEFTOVER_NAME_STOPWORDS.has(token));
}

function namesLooselyMatch(left: string, right: string): boolean {
  if (!left || !right) return false;
  if (left === right) return true;
  const leftTokens = distinctiveLeftoverNameTokens(left);
  const rightTokens = distinctiveLeftoverNameTokens(right);
  return leftTokens.some((token) => rightTokens.includes(token));
}

/** Match leftover Mackay to the admin/user-preferred row (NPI, then id, then name). */
export function findPreferredRowForLeftover(
  leftover: any,
  preferredRows: any[],
): any | null {
  if (!leftover || !Array.isArray(preferredRows) || preferredRows.length === 0) {
    return null;
  }
  const npi = leftoverFacilityNpi(leftover);
  const name = leftoverFacilityName(leftover);
  const fid = leftoverFacilityId(leftover);
  const matches = preferredRows.filter((row) => {
    if (!row || row.is_delete === true) return false;
    const rowNpi = leftoverFacilityNpi(row);
    const rowName = leftoverFacilityName(row);
    const rowFid = leftoverFacilityId(row);
    if (npi && rowNpi && npi === rowNpi) return true;
    if (fid != null && rowFid != null && fid === rowFid) return true;
    return namesLooselyMatch(name, rowName);
  });
  return (
    matches.find((row) => leftoverDiscountCode(row)) || matches[0] || null
  );
}

/**
 * Copy admin-row promo + preferred PK onto leftover so Pay Now bypasses
 * Stripe and checkout targets the leftover preferred row, not a sibling.
 */
export function mergeAdminPromoIntoLeftoverFacility(
  leftover: any,
  preferredRows: any[],
): any {
  if (!leftover) return leftover;
  const match = findPreferredRowForLeftover(leftover, preferredRows);
  if (!match) return withSnfCheckoutType(leftover) ?? leftover;
  const promo = leftoverDiscountCode(match) || leftoverDiscountCode(leftover);
  const leftoverNpi =
    leftoverFacilityNpi(leftover) || leftoverFacilityNpi(match);
  const details: Record<string, unknown> = {
    ...facilityDetails(leftover),
    ...facilityDetails(match),
    ...(leftoverNpi ? { ccn_number: leftoverNpi, npi_number: leftoverNpi } : {}),
  };
  const rulesId = leftoverFacilityId(leftover) ?? leftoverFacilityId(match);
  const preferredPk = Number(match?.id);
  return withSnfCheckoutType({
    ...leftover,
    facility_id: rulesId,
    user_preferred_facility_id:
      Number.isFinite(preferredPk) && preferredPk > 0 ? preferredPk : undefined,
    id:
      Number.isFinite(preferredPk) && preferredPk > 0
        ? preferredPk
        : leftover?.id ?? rulesId,
    user_id: match?.user_id ?? leftover?.user_id,
    discount_coupon_name: promo || leftover?.discount_coupon_name,
    discount_coupon_id:
      leftoverDiscountCouponId(match) ?? leftoverDiscountCouponId(leftover),
    discount_end_date:
      match?.discount_end_date ?? leftover?.discount_end_date ?? null,
    ccn_number: leftoverNpi || leftover?.ccn_number || match?.ccn_number || null,
    npi_number: leftoverNpi || leftover?.npi_number || match?.npi_number || null,
    facility_name:
      leftover?.facility_name ||
      leftover?.site_name ||
      match?.facility_name ||
      details.facility_name,
    vertical:
      leftover?.vertical ??
      details.vertical ??
      match?.vertical ??
      "healthcare",
    facility_details: details,
  });
}

/** Promo for Pay Now: only a code the user typed or applied. */
export function resolveLeftoverPayNowPromo(args: {
  appliedPromo?: string | null;
  facilityDiscountCode?: string | null;
}): string | null {
  const applied = String(args.appliedPromo ?? "").trim();
  return applied || null;
}

/** Successful Pay Now with applied or admin-row promo lifts the unpaid wall. */
export function shouldClearAccessRestrictedAfterPromoPayNow(args: {
  appliedPromo?: string | null;
  facilityDiscountCode?: string | null;
  checkoutSuccess: boolean;
}): boolean {
  return (
    args.checkoutSuccess &&
    !!resolveLeftoverPayNowPromo({
      appliedPromo: args.appliedPromo,
      facilityDiscountCode: args.facilityDiscountCode,
    })
  );
}

function readClearedIdentities(): PromoCheckoutClearedIdentity[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(PROMO_CHECKOUT_CLEARED_RESTRICTED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function markPromoCheckoutClearedRestricted(facility: any): void {
  if (typeof window === "undefined" || !facility) return;
  const identity = leftoverIdentity(facility);
  if (!identity.npi && !identity.facilityName && identity.facilityId == null) {
    return;
  }
  const next = readClearedIdentities().filter(
    (stored) => !identitiesMatch(stored, facility),
  );
  next.push(identity);
  try {
    sessionStorage.setItem(
      PROMO_CHECKOUT_CLEARED_RESTRICTED_KEY,
      JSON.stringify(next),
    );
  } catch {
    // ignore quota / private-mode failures
  }
}

export function isPromoCheckoutClearedForFacility(facility: any): boolean {
  if (!facility) return false;
  return readClearedIdentities().some((stored) => identitiesMatch(stored, facility));
}

/**
 * After promo Pay Now: leftover Mackay stays selected (active).
 * Incomplete leftover unpaid: reopen Select Plan, not the dead-end wall.
 * True trial / payment-failed expiry: Access Restricted.
 */
export function resolveUnpaidAccessAction(facility: any): UnpaidAccessAction {
  if (!facility) return "none";
  if (isPromoCheckoutClearedForFacility(facility)) return "active";
  if (isLeftoverUnpaidFacility(facility)) return "select-plan";
  // Cancelled (is_restricted) or expired plan → Access Restricted wall
  if (isAccessRestrictedFacility(facility)) return "restricted";
  return "none";
}

export function shouldNavigateToRestrictedFacility(facility: any): boolean {
  return resolveUnpaidAccessAction(facility) === "restricted";
}

export function shouldNavigateToSelectPlanForUnpaid(facility: any): boolean {
  return resolveUnpaidAccessAction(facility) === "select-plan";
}

export function readSelectedPlanIdFromSession(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem("selected_plan");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown };
    const id = Number(parsed?.id ?? raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/**
 * Unpaid Access Restricted "Update Payment" must reopen Select Plan / Order Summary.
 * Never create a Stripe session from the wall (leftover already-complete skip).
 */
export function resolveRestrictedUpdatePaymentAction(input?: {
  selectedPlanId?: string | number | null;
  preferredFacilityId?: number | null;
}): RestrictedUpdatePaymentAction {
  const rawPlanId = input?.selectedPlanId ?? readSelectedPlanIdFromSession();
  const planId = Number(rawPlanId);
  const preferredFacilityId =
    typeof input?.preferredFacilityId === "number" &&
    Number.isFinite(input.preferredFacilityId) &&
    input.preferredFacilityId > 0
      ? input.preferredFacilityId
      : undefined;
  const state: RestrictedUpdatePaymentAction["state"] = {
    fromRestrictedUpdatePayment: true,
    ...(preferredFacilityId != null ? { fromPreferredFacilityId: preferredFacilityId } : {}),
  };
  if (Number.isFinite(planId) && planId > 0) {
    return {
      pathname: "/dashboard/pricing/order-summary",
      search: `?selected_plan_id=${encodeURIComponent(String(planId))}`,
      state,
    };
  }
  return {
    pathname: "/dashboard/pricing",
    search: "",
    state,
  };
}

/** Persist leftover unpaid so Select Plan / Order Summary auto-apply the admin promo. */
export function persistPreferredFacilityForSelectPlan(facility: any): void {
  if (!facility) return;
  const details = facilityDetails(facility);
  const facilityTypeId = Number(
    facility?.facility_type_id ?? details.facility_type_id ?? 0,
  );
  let existingDiscount: string | null = null;
  let existingPreferredPk: number | null = null;
  let sameFacility = false;
  try {
    const existing = readPreferredFacilityForPricing(null);
    sameFacility = !!existing && leftoverFacilitiesMatch(existing, facility);
    if (sameFacility) {
      const raw = existing?.discount_coupon_name;
      existingDiscount =
        typeof raw === "string" && raw.trim() ? raw.trim() : null;
      const existingPk = Number(existing?.user_preferred_facility_id);
      existingPreferredPk =
        Number.isFinite(existingPk) && existingPk > 0 ? existingPk : null;
    }
  } catch {
    existingDiscount = null;
    existingPreferredPk = null;
  }
  const discountFromFacility = leftoverDiscountCode(facility);
  const leftoverNpi = leftoverFacilityNpi(facility);
  const rulesId = leftoverFacilityId(facility);
  const preferredPk = leftoverPreferredPk(facility);
  // Never keep sibling facility preferred PK / promo across multi-pending clicks.
  const persistPreferredPk = preferredPk ?? (sameFacility ? existingPreferredPk : null);
  const persistId =
    persistPreferredPk ?? rulesId ?? facility?.id;
  const typed = withSnfCheckoutType({
    ...facility,
    facility_type: facility?.facility_type || details.facility_type || "",
    facility_type_id: Number.isFinite(facilityTypeId) && facilityTypeId > 0
      ? facilityTypeId
      : null,
    facility_type_slug:
      facility?.facility_type_slug || details.facility_type_slug || "",
    primary_profile_slug:
      facility?.primary_profile_slug || details.primary_profile_slug || "",
    facility_details: {
      ...details,
      ...(leftoverNpi ? { ccn_number: leftoverNpi, npi_number: leftoverNpi } : {}),
    },
  }) ?? facility;
  const typedDetails = facilityDetails(typed);
  writePreferredFacilityForPricing({
    id: persistId,
    facility_id: rulesId,
    user_preferred_facility_id: persistPreferredPk ?? undefined,
    user_id: facility?.user_id,
    is_verified: facility?.is_verified,
    is_active: facility?.is_active,
    facility_name:
      facility?.facility_name ||
      facility?.site_name ||
      details.facility_name ||
      "",
    facility_type: typed?.facility_type || typedDetails.facility_type || "",
    facility_type_id: toPersistedTypeId(typed?.facility_type_id ?? typedDetails.facility_type_id),
    facility_type_slug:
      typed?.facility_type_slug || typedDetails.facility_type_slug || undefined,
    primary_profile_slug:
      typed?.primary_profile_slug || typedDetails.primary_profile_slug || undefined,
    discount_coupon_name:
      discountFromFacility || (sameFacility ? existingDiscount : null),
    discount_end_date:
      facility?.discount_end_date ?? details.discount_end_date ?? null,
    ccn_number:
      leftoverNpi ||
      facility?.ccn_number ||
      details.ccn_number ||
      facility?.npi_number ||
      null,
    npi_number: leftoverNpi || facility?.npi_number || details.npi_number || null,
    city: facility?.city ?? details.city ?? null,
    state: facility?.state ?? details.state ?? null,
    vertical: facility?.vertical ?? details.vertical ?? "healthcare",
    facility_details: typedDetails,
  });
}

/** Keep leftover Mackay identity across sibling Arco switch / wall. */
export function persistLeftoverUnpaidIdentity(facility: any): void {
  if (typeof window === "undefined" || !facility) return;
  const npi = leftoverFacilityNpi(facility);
  const typed = withSnfCheckoutType(facility) ?? facility;
  const details = facilityDetails(typed);
  const snapshot = {
    facility_id: leftoverFacilityId(facility),
    user_preferred_facility_id: leftoverPreferredPk(facility),
    facility_name:
      facility?.facility_name ||
      facility?.site_name ||
      details.facility_name ||
      "",
    facility_type: typed?.facility_type || details.facility_type || "",
    facility_type_id: toPersistedTypeId(
      typed?.facility_type_id ?? details.facility_type_id,
    ),
    facility_type_slug:
      typed?.facility_type_slug || details.facility_type_slug || undefined,
    primary_profile_slug:
      typed?.primary_profile_slug || details.primary_profile_slug || undefined,
    primary_taxonomy_code:
      typed?.primary_taxonomy_code || details.primary_taxonomy_code || undefined,
    primary_taxonomy_desc:
      typed?.primary_taxonomy_desc || details.primary_taxonomy_desc || undefined,
    ccn_number: npi || facility?.ccn_number || null,
    npi_number: npi || facility?.npi_number || null,
    plan_id: facility?.plan_id ?? details.pricing_tier_id,
    is_plan_active: facility?.is_plan_active,
    plan_status: facility?.plan_status,
    transaction_status: facility?.transaction_status,
    is_trial: facility?.is_trial ?? facility?.is_trail,
    discount_coupon_name: leftoverDiscountCode(facility) || null,
    discount_coupon_id: leftoverDiscountCouponId(facility),
    city: facility?.city ?? details.city ?? null,
    state: facility?.state ?? details.state ?? null,
    vertical: facility?.vertical ?? details.vertical ?? "healthcare",
    facility_details: details,
  };
  try {
    sessionStorage.setItem(LEFTOVER_UNPAID_FACILITY_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore quota / private-mode failures
  }
}

export function readLeftoverUnpaidIdentity(): any | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(LEFTOVER_UNPAID_FACILITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Prefer leftover unpaid Mackay (NPI / leftover snapshot) over paid sibling Arco.
 */
export function pickLeftoverUnpaidSource(...sources: any[]): any | null {
  const list = sources.filter((source) => source != null);
  const withLeftoverNpi = list.find((source) => {
    const npi = leftoverFacilityNpi(source);
    return npi.length === 10 && isLeftoverUnpaidFacility(source);
  });
  if (withLeftoverNpi) return withLeftoverNpi;
  const leftover = list.find((source) => isLeftoverUnpaidFacility(source));
  if (leftover) return leftover;
  const withNpi = list.find((source) => leftoverFacilityNpi(source).length === 10);
  return withNpi || list[0] || null;
}

export function clearRestrictedFacilitySession(): void {
  if (typeof window === "undefined") return;
  for (const key of [
    "restricted_facility_name",
    "restricted_has_multiple_facilities",
    "restricted_message_variant",
    "restricted_plan_id",
  ]) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

export function navigateToSelectPlanForUnpaidFacility(
  navigate: NavigateFunction,
  facility: any,
  extras?: { selectedPlanId?: string | number | null },
): void {
  clearLeftoverUnpaidIfDifferentFacility(facility);
  persistLeftoverUnpaidIdentity(facility);
  persistPreferredFacilityForSelectPlan(facility);
  clearRestrictedFacilitySession();
  const preferredFacilityId =
    leftoverPreferredPk(facility) ?? leftoverFacilityId(facility) ?? (
      Number.isFinite(Number(facility?.id)) && Number(facility?.id) > 0
        ? Number(facility.id)
        : undefined
    );
  const planId =
    extras?.selectedPlanId ??
    facility?.plan_id ??
    facilityDetails(facility).pricing_tier_id ??
    readSelectedPlanIdFromSession();
  const action = resolveRestrictedUpdatePaymentAction({
    selectedPlanId: planId as string | number | null,
    preferredFacilityId,
  });
  navigate(`${action.pathname}${action.search}`, { state: action.state });
}
